import React from "react";

import { deleteEmployee } from "EmployerApplication/Actions";
import { saveAddEmployeesStepComplete, useGetCurrentCompanyApplicationQuery } from "EmployerApplication/api";
import AddEmployeeModal from "EmployerApplication/Components/AddEmployeeModal";
import EmployeeTable from "EmployerApplication/Components/EmployeeTable";
import { getHasMinimumEmployees } from "EmployerApplication/MinimumEmployeesUtils";
import { getEmployees } from "EmployerApplication/Selectors";
import { companyInformationURL, selectContributionTiersURL, reviewEmployeeEmailsURL } from "EmployerApplication/urls";
import { connect } from "react-redux";
import { withRouter } from "react-router-dom";
import getTooltip from "Utils/getTooltip";

import Button from "Cobalt/Button";
import { Callout } from "Cobalt/Callout";
import { EmptyState } from "Cobalt/EmptyState";
import { TitleSmall } from "Cobalt/Header";
import { StandaloneTrigger } from "Cobalt/Modal";
import { Row, Col } from "Common/Grid";
import { Div } from "Common/HTML";
import InfoPopover from "Common/InfoPopover";
import NavigationControls from "Common/NavigationControls";
import { Body2 } from "Theme/BodyText";
import { SurfaceSection } from "Theme/Surface";

import makeBem from "Utils/makeBem";

export const bem = makeBem("AddEmployees");

const IncludeAllEmployeeMessage = () => (
  <Callout
    type="info"
    showIcon
    body={
      <>
        Be sure to add&nbsp;
        <b>
          <u>all</u>
        </b>
        &nbsp;eligible employees and owners, even if they don't need insurance.&nbsp;
        <InfoPopover
          infoTitle="Leigh Says..."
          infoText={getTooltip("Required Employee Documentation")}
          className={bem("toolTip")}
          displayComponent={<span className={bem("toolTipDisplay")}>(Why?)</span>}
          placement="right"
        />
      </>
    }
  />
);

const OwnerOrEmployeesCopy = () => <>You must add at least two employees or one full-time owner to continue.</>;

const OwnerOrEmployeesMessage = () => <Callout type="info" body=<OwnerOrEmployeesCopy /> />;

const EmployeesOnlyCopy = () => (
  <>
    You must add at least two employees to continue.&nbsp;
    <InfoPopover
      infoText={getTooltip("Two Employees Required")}
      className={bem("toolTip")}
      displayComponent={<span className={bem("toolTipDisplay")}>(Why?)</span>}
      placement="right"
    />
  </>
);

const EmployeesOnlyMessage = () => <Callout type="warning" body=<EmployeesOnlyCopy /> />;

const FewEmployeesMessage = ({ hasMinimumEmployees, allowSingleOwner }) => {
  if (hasMinimumEmployees) {
    return <IncludeAllEmployeeMessage />;
  }
  if (allowSingleOwner && !hasMinimumEmployees) {
    return <OwnerOrEmployeesMessage />;
  }
  return <EmployeesOnlyMessage />;
};

const AddEmployeesButton = (buttonProps) => (
  <StandaloneTrigger
    trigger={(props) => (
      <Button
        color="secondary"
        disabled={props.disabled}
        onClick={(e) => {
          props.onClick(e);
        }}
        {...buttonProps}
      >
        Add Employee
      </Button>
    )}
    modal={(props) => <AddEmployeeModal {...props} />}
  />
);

const NoEmployeesCard = ({ allowSingleOwner }) => (
  <EmptyState icon="user-plus" title="No employees added yet." titleProps={{ size: "body2" }} className={bem("NoEmployees")} p={4} pt={4}>
    <Body2 className={bem("NoEmployees", "subtitle")} mt={3} mb={3}>
      {allowSingleOwner ? <OwnerOrEmployeesCopy /> : <EmployeesOnlyCopy />}
    </Body2>
    <AddEmployeesButton cobaltProps={{ color: "tertiary" }} classicProps={{ color: "link" }} size="medium" />
  </EmptyState>
);

export const AddEmployeesDumb = ({ deleteEmployee, employees, history }) => {
  const { data: companyApplication, isLoading } = useGetCurrentCompanyApplicationQuery();

  if (isLoading) return <div />;

  const { allowSingleOwner } = companyApplication;
  const { hasTieredContributions } = companyApplication;

  const hasMinimumEmployees = getHasMinimumEmployees(employees, allowSingleOwner);

  return (
    <div>
      <SurfaceSection>
        <Row>
          <Col>
            {employees.length == 0 ? (
              <NoEmployeesCard allowSingleOwner={allowSingleOwner} />
            ) : (
              <Div mb={4}>
                <Div mb={6}>
                  <FewEmployeesMessage allowSingleOwner={allowSingleOwner} hasMinimumEmployees={hasMinimumEmployees} />
                </Div>
                <TitleSmall mb={3}>Eligible Employees</TitleSmall>
                <EmployeeTable editPage={false} onDeleteEmployee={deleteEmployee} employees={employees} />
                <AddEmployeesButton mt={4} />
              </Div>
            )}
          </Col>
        </Row>
      </SurfaceSection>
      <NavigationControls
        backLink={companyInformationURL}
        onClickSave={() => {
          saveAddEmployeesStepComplete()
            .then((response) => {
              if (response.ok) {
                return response.json();
              }
              throw new Error(`Unable to save, HTTP ${response.status}`);
            })
            .then(() => {
              history.push(hasTieredContributions ? selectContributionTiersURL : reviewEmployeeEmailsURL);
            });
        }}
        saveDisabled={!hasMinimumEmployees}
      />
    </div>
  );
};

const mapStateToProps = (state) => ({
  employees: getEmployees(state),
});

const mapDispatchToProps = {
  deleteEmployee,
};

const withRedux = connect(mapStateToProps, mapDispatchToProps);

export default withRouter(withRedux(AddEmployeesDumb));
